import { Link } from 'react-router-dom';

/**
 * Official ASTIIV BASGAT Logo component.
 *
 * Props:
 * - variant: 'lockup' (default) | 'icon' | 'wordmark'
 * - size: 'sm' | 'md' | 'lg' | 'xl' (icon dimensions + wordmark text size)
 * - to: string (if provided, wraps the logo in a <Link>; null for static)
 * - sublabel: optional small string shown next to wordmark (e.g. "INDIA")
 * - wordmarkClassName: override classes for the wordmark text
 * - className: wrapper classes
 * - tracking: tracking class for the wordmark (default 'tracking-[0.1em]')
 *
 * The icon image is served from /dreamlife-logo.png (public folder).
 */

const SIZE_MAP = {
  xs: { icon: 'w-5 h-5', text: 'text-base' },
  sm: { icon: 'w-7 h-7', text: 'text-lg' },
  md: { icon: 'w-9 h-9', text: 'text-xl' },
  lg: { icon: 'w-11 h-11', text: 'text-2xl' },
  xl: { icon: 'w-14 h-14', text: 'text-3xl' },
};

const serif = { fontFamily: "'Playfair Display', serif" };

export const Logo = ({
  variant = 'lockup',
  size = 'md',
  to = '/',
  sublabel,
  wordmarkClassName = 'text-white',
  className = '',
  tracking = 'tracking-[0.12em]',
  testId = 'dreamlife-logo',
}) => {
  const s = SIZE_MAP[size] || SIZE_MAP.md;

  const Icon = (
    <span
      className={`dl-logo-icon inline-flex items-center justify-center ${s.icon} rounded-md overflow-hidden ring-1 ring-[#C6A15B]/25 bg-black flex-shrink-0 relative`}
      aria-hidden="true"
    >
      <img
        src="/astiiv-logo.png"
        alt=""
        className="w-full h-full object-cover"
        loading="eager"
        decoding="async"
      />
      <span className="dl-logo-shimmer pointer-events-none absolute inset-0" />
    </span>
  );

  const Wordmark = (
    <span
      className={`font-semibold ${s.text} ${tracking} ${wordmarkClassName} whitespace-nowrap`}
      style={serif}
    >
      ASTIIV BASGAT
      {sublabel && (
        <span className="text-white/30 text-[10px] tracking-[0.2em] ml-2 align-middle">
          {sublabel}
        </span>
      )}
    </span>
  );

  const content = (
    <span className={`inline-flex items-center gap-2.5 ${className}`} data-testid={testId}>
      {variant !== 'wordmark' && Icon}
      {variant !== 'icon' && Wordmark}
      <span className="sr-only">ASTIIV BASGAT</span>
    </span>
  );

  if (to) {
    return (
      <Link to={to} className="inline-flex items-center" aria-label="ASTIIV BASGAT home">
        {content}
      </Link>
    );
  }

  return content;
};

export default Logo;
